import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import { useMemo } from 'react';
import { css, useTheme, jsx } from '@emotion/react';
import { CriticalIcon, CheckmarkBackgroundSmIcon, ClockIcon, CriticalSmIcon, PreferredSelectedIcon } from '@paypalcorp/pp-react-icons';
import { devWarning, utilStyles, getColorFamily, ColorProvider } from '@paypalcorp/pp-react-utils';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { math } from 'polished';
import { textStyles } from '@paypalcorp/pp-react-text';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysSpaceXs = tokens.sysSpaceXs,
      sysSpaceSm = tokens.sysSpaceSm,
      sysSizeIconXs = tokens.sysSizeIconXs,
      sysSpaceTiny = tokens.sysSpaceTiny,
      sysBorderRadiusMd = tokens.sysBorderRadiusMd,
      sysBorderThicknessEmphasis = tokens.sysBorderThicknessEmphasis,
      sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
      sysSizeCircleXs = tokens.sysSizeCircleXs,
      sysColorErrorMain = tokens.sysColorErrorMain,
      sysColorErrorContrast = tokens.sysColorErrorContrast,
      sysColorWarningMain = tokens.sysColorWarningMain,
      sysColorWarningContrast = tokens.sysColorWarningContrast,
      sysColorSuccessMain = tokens.sysColorSuccessMain,
      sysColorSuccessContrast = tokens.sysColorSuccessContrast,
      sysColorInfoMain = tokens.sysColorInfoMain,
      sysColorInfoContrast = tokens.sysColorInfoContrast,
      sysColorSpecialtyMain = tokens.sysColorSpecialtyMain,
      sysColorSpecialtyContrast = tokens.sysColorSpecialtyContrast,
      sysColorBackgroundMain = tokens.sysColorBackgroundMain,
      sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
      sysColorWarningAlt = tokens.sysColorWarningAlt;

  var _textStyles = textStyles(tokens),
      textCaption = _textStyles.caption;

  var _utilStyles = utilStyles(tokens),
      screenReader = _utilStyles.screenReader;

  // 288px
  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: badge_base;\n    ", "\n    max-width: ", ";\n    overflow: hidden;\n    word-break: break-word;\n    text-transform: none;\n    -webkit-line-clamp: 2;\n    display: -webkit-inline-box;\n    -webkit-box-orient: vertical;\n    height: auto;\n    padding: ", " ", ";\n    border-radius: ", ";\n    [dir='rtl'] & {\n      text-align: right;\n    }\n  "])), textCaption(), '18rem', sysSpaceTiny, sysSpaceSm, sysBorderRadiusMd);

  var badgeColor = function (backgroundColor, color) {
    return css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n      color: ", ";\n      background-color: ", ";\n    "])), color, backgroundColor);
  };

  var iconBadge = function (color) {
    return css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n      color: ", ";\n      width: ", ";\n      height: ", ";\n      padding: ", ";\n      background-color: ", ";\n      border: ", " solid ", ";\n      border-radius: 50%;\n    "])), color, sysSizeCircleXs, sysSizeCircleXs, sysBorderThicknessNormal, sysColorBackgroundMain, sysBorderThicknessEmphasis, sysColorBackgroundSurface);
  };

  var badgeType = function (type) {
    var colorMap = {
      feature: badgeColor(sysColorSpecialtyMain, sysColorSpecialtyContrast),
      success: badgeColor(sysColorSuccessMain, sysColorSuccessContrast),
      warning: badgeColor(sysColorWarningMain, sysColorWarningContrast),
      critical: badgeColor(sysColorErrorMain, sysColorErrorContrast),
      neutral: badgeColor(sysColorInfoMain, sysColorInfoContrast)
    };
    return css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n      label: ", ";\n      ", ";\n    "])), type, colorMap[type]);
  };

  var paddingLeft = math("".concat(sysSpaceSm, " + ").concat(sysSizeIconXs, " + ").concat(sysSpaceTiny));
  var leftIcon = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: left_icon;\n    position: relative;\n    padding-inline-start: ", ";\n  "])), paddingLeft); //Note: additional 1px added to align the icon with the text.

  var iconVerticalSpacing = math("".concat(sysSpaceTiny, " + ").concat(sysBorderThicknessNormal));
  var icon = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: icon;\n    position: absolute;\n    inset-block: ", ";\n    inset-inline: ", " auto;\n  "])), iconVerticalSpacing, sysSpaceSm);
  var authorized = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: authorized;\n    ", ";\n  "])), iconBadge(sysColorSuccessMain));
  var inProgress = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: in_progress;\n    ", ";\n  "])), iconBadge(sysColorWarningAlt));
  var onHold = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: on_hold;\n    ", ";\n  "])), iconBadge(sysColorErrorMain));
  var favorite = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: favorite;\n    ", ";\n  "])), iconBadge(sysColorSpecialtyMain));
  var numeric = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: numeric;\n    color: ", ";\n    background-color: ", ";\n    padding: 0 ", ";\n    height: ", ";\n    min-width: ", ";\n    border: ", " solid ", ";\n    border-radius: ", ";\n    text-align: center;\n    [dir='rtl'] & {\n      text-align: center;\n    }\n  "])), sysColorErrorContrast, sysColorErrorMain, sysSpaceXs, sysSizeCircleXs, sysSizeCircleXs, sysBorderThicknessEmphasis, sysColorBackgroundSurface, math("".concat(sysSizeCircleXs, "/2")));
  return {
    base: base,
    badgeType: badgeType,
    leftIcon: leftIcon,
    icon: icon,
    screenReader: screenReader,
    authorized: authorized,
    inProgress: inProgress,
    onHold: onHold,
    favorite: favorite,
    numeric: numeric
  };
}

var _excluded = ["type", "status", "className", "leftIcon", "leftIconLabel", "children"],
    _excluded2 = ["className", "children", "label", "icon"];

function useStyles() {
  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  return _styles;
}

var Badge = function (_ref) {
  var _styles$badgeType;

  var _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'feature' : _ref$type,
      status = _ref.status,
      className = _ref.className,
      leftIcon = _ref.leftIcon,
      leftIconLabel = _ref.leftIconLabel,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _styles = useStyles();

  var tokens = useTheme();
  var colorFamily = getColorFamily({
    neutral: 'Info',
    warning: 'Warning',
    success: 'Success',
    critical: 'Error',
    feature: 'Specialty'
  }[type], tokens);
  var badgeCssVal = [_styles.base, (_styles$badgeType = _styles.badgeType) === null || _styles$badgeType === void 0 ? void 0 : _styles$badgeType.call(_styles, type), (leftIcon || leftIconLabel) && _styles.leftIcon]; //Badge with icon

  var ariaProps = {
    'aria-hidden': true
  };
  var badgeIcon = (leftIcon || leftIconLabel) && jsx("span", {
    css: _styles.icon,
    "data-ppui": "true"
  }, jsx(function Icon() {
    /* istanbul ignore next */
    if (type === 'critical') {
      return jsx(CriticalIcon, _extends({
        size: "xs"
      }, ariaProps, {
        "data-ppui": "true"
      }));
    }

    return jsx(leftIcon, _extends({
      size: "xs"
    }, ariaProps, {
      "data-ppui": "true"
    }));
  }, {
    "data-ppui": "true"
  }), jsx("span", {
    css: _styles.screenReader,
    "data-ppui": "true"
  }, leftIconLabel)); // End badge with icon

  return jsx(ColorProvider, {
    colorFamily: colorFamily,
    "data-ppui": "true"
  }, jsx("span", _extends({
    css: badgeCssVal,
    className: className,
    "data-ppui-info": "badge_3.4.5"
  }, rest), badgeIcon, children));
}; //Authorized Badge


var AuthorizedBadge = function (props) {
  var _useStyles = useStyles(),
      authorized = _useStyles.authorized;

  return jsx(BadgeComponent, _extends({}, props, {
    css: authorized,
    className: props.className,
    icon: jsx(CheckmarkBackgroundSmIcon, {
      size: "xs",
      "aria-hidden": "true",
      "data-ppui": "true"
    }),
    "data-ppui": "true"
  }));
}; //InProgress Badge

var InProgressBadge = function (props) {
  var _useStyles2 = useStyles(),
      inProgress = _useStyles2.inProgress;

  return jsx(BadgeComponent, _extends({}, props, {
    css: inProgress,
    className: props.className,
    icon: jsx(ClockIcon, {
      size: "xs",
      "aria-hidden": "true",
      "data-ppui": "true"
    }),
    "data-ppui": "true"
  }));
}; //OnHold Badge

var OnHoldBadge = function (props) {
  var _useStyles3 = useStyles(),
      onHold = _useStyles3.onHold;

  return jsx(BadgeComponent, _extends({}, props, {
    css: onHold,
    className: props.className,
    icon: jsx(CriticalSmIcon, {
      size: "xs",
      "aria-hidden": "true",
      "data-ppui": "true"
    }),
    "data-ppui": "true"
  }));
}; //Favorite Badge

var FavoriteBadge = function (props) {
  var _useStyles4 = useStyles(),
      favorite = _useStyles4.favorite;

  return jsx(BadgeComponent, _extends({}, props, {
    css: favorite,
    className: props.className,
    icon: jsx(PreferredSelectedIcon, {
      size: "xs",
      "aria-hidden": "true",
      "data-ppui": "true"
    }),
    "data-ppui": "true"
  }));
}; //Numeric Badge

var NumericBadge = function (props) {
  //@ts-ignore
  var numericProp = parseInt(props.children, 10);
  var numericValue = numericProp < 10 ? numericProp : '9+';

  var _useStyles5 = useStyles(),
      numeric = _useStyles5.numeric;

  return jsx(BadgeComponent, _extends({}, props, {
    css: numeric,
    "data-ppui": "true"
  }), numericValue);
};

var BadgeComponent = function (_ref2) {
  var className = _ref2.className,
      children = _ref2.children,
      label = _ref2.label,
      icon = _ref2.icon,
      rest = _objectWithoutProperties(_ref2, _excluded2);

  var _useStyles6 = useStyles(),
      base = _useStyles6.base,
      screenReader = _useStyles6.screenReader;

  return jsx("span", _extends({
    css: base,
    className: className,
    "data-ppui-info": "badge_3.4.5"
  }, rest), icon, icon ? jsx("span", {
    css: screenReader,
    "data-ppui": "true"
  }, label) : children);
};

export { AuthorizedBadge, Badge, FavoriteBadge, InProgressBadge, NumericBadge, OnHoldBadge, styles as badgeStyles };
